import React from 'react';
import { useLegacy } from '../contexts/LegacyContext';
import { useWallet } from '../contexts/WalletContext';
import { Plus, Grid, Clock, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const DashboardPage: React.FC = () => {
  const { items, collections, isLoading } = useLegacy();
  const { address, balance } = useWallet();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-primary-900 mb-2">
          Your <span className="text-accent-wood">Legacy</span> Dashboard
        </h1>
        <p className="text-primary-600">
          Welcome back! Here's an overview of your precious items and collections.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-600">Total Items</p>
              <p className="text-2xl font-bold text-primary-900">{items.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Grid className="w-6 h-6 text-primary-700" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-600">Collections</p>
              <p className="text-2xl font-bold text-primary-900">{collections.length}</p>
            </div>
            <div className="w-12 h-12 bg-accent-wood/10 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-accent-wood" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-primary-900">
                {balance ? `${parseFloat(balance).toFixed(4)}` : '0.0000'}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-700" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-600">Recent Activity</p>
              <p className="text-2xl font-bold text-primary-900">
                {items.filter(item => {
                  const itemDate = new Date(item.mintDate);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return itemDate > weekAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-primary-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm mb-8">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/mint"
            className="inline-flex items-center gap-2 bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Mint New Item
          </Link>
          <Link
            to="/collection"
            className="inline-flex items-center gap-2 border border-accent-wood text-accent-wood hover:bg-accent-wood hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Grid className="w-5 h-5" />
            View Collections
          </Link>
          <Link
            to="/timeline"
            className="inline-flex items-center gap-2 border border-primary-300 text-primary-700 hover:bg-primary-50 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Clock className="w-5 h-5" />
            View Timeline
          </Link>
        </div>
      </div>

      {/* Recent Items */}
      <div className="bg-white p-6 rounded-xl border border-primary-200 shadow-sm">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">Recent Items</h2>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-accent-wood border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-primary-600 mt-2">Loading your items...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-primary-700" />
            </div>
            <p className="text-primary-600 mb-4">You haven't minted any items yet.</p>
            <Link
              to="/mint"
              className="inline-flex items-center gap-2 bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              Mint Your First Item
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.slice(0, 6).map((item) => (
              <div key={item.id} className="border border-primary-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                <div className="aspect-square bg-primary-100 rounded-lg mb-4 flex items-center justify-center">
                  {item.imageUrl ? (
                    <img 
                      src={item.imageUrl} 
                      alt={item.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <div className="text-primary-400 text-4xl">📷</div>
                  )}
                </div>
                <h3 className="font-semibold text-primary-900 mb-2">{item.name}</h3>
                <p className="text-sm text-primary-600 mb-2 line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between text-sm text-primary-500">
                  <span>{item.category}</span>
                  <span>{new Date(item.mintDate).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage; 